import { canDownload, clientStatusLabel, isReviewable, mediaLink, metaLabel } from './deliverables'
import { toDrivePreviewUrl } from './drive'
import { derivedBlocks, type BlockKey } from './clientPage'
import type { Deliverable, FileRow } from '../types/portal'

/**
 * WHAT THE CLIENT PAGE DRAWS, AND IN WHAT ORDER.
 *
 * Lifted out of `pages/client/ProjectPage.tsx` on 14 Oct 2026, partly to
 * keep that file under the size this editor can write back in one piece and
 * partly because these are rules, not markup, and they are easier to argue
 * about on their own.
 */

/**
 * Is this block on for this project?
 *
 * **THE STORED TICKS ADD; THEY NEVER SUBTRACT. 17 Oct 2026.**
 *
 * This used to be `if (stored) return stored.includes(key)` — a stored list
 * won outright. That was safe while `page_blocks` was null on every project
 * and only ever written by someone deliberately ticking. Then the setup
 * flow started writing it on every new project, and the rule quietly became
 * something nobody intended: **the shape of a client's page was frozen at
 * the minute the job was created.**
 *
 * Set a project up as films-only, add a photography set to it a fortnight
 * later, and the gallery never appeared — the piece was there, visible,
 * approved, and the page had no block to draw it in. Same for a transcript,
 * the first shoot date, the first tracker step. Tom found this looking for
 * what "doubles up or gets in the way", and it is the worst of them,
 * because nothing anywhere says it is happening.
 *
 * So the two sources are now unioned. The derived set is what the project
 * ACTUALLY HAS; the stored set is what somebody PROMISED it would have —
 * a note, an outline, a wrap-up report, blocks that cannot be derived
 * because they are written rather than counted. A page shows both.
 *
 * The rule it has to keep obeying: **a ticked block with nothing in it
 * draws nothing.** Unioning cannot therefore add clutter — it can only stop
 * work that exists from being invisible.
 *
 * What this gives up is the ability to tick a block OFF. Nothing in the OS
 * can do that today — the checklist panel that could was built and taken
 * straight back out on Tom's word (see PortalTeam.tsx) — so nothing is lost
 * now. If a way to hide a block is ever wanted, it needs its own list of
 * deliberate exclusions rather than the absence of a tick, because absence
 * is also what every legacy project has.
 */
export function blockOn(
  stored: BlockKey[] | null,
  key: BlockKey,
  deliverables: Deliverable[],
  stepCount: number,
  shootCount: number,
  complete: boolean,
): boolean {
  const derived = derivedBlocks(deliverables, stepCount, shootCount, complete)
  if (derived.includes(key)) return true
  return stored ? stored.includes(key) : false
}

/**
 * WHAT LEADS THE PAGE — whatever is waiting on them.
 *
 * This is the Bloomberg fault. The page used to lead with the first
 * reviewable item, or failing that the first video in the studio's own
 * order, which meant episode two could go up and the page would still open
 * on episode one. Tom: *"it was getting messy to put the next one up,
 * because they couldn't see it."*
 *
 * The order now:
 *   1. anything waiting for their approval, latest first
 *   2. anything not yet approved
 *   3. the latest thing there is
 *
 * "Latest" is the studio's own running order read from the end, which is
 * where a new episode is added. A stored date would be better and is the
 * next thing to add; nothing here guesses at one.
 */
export function leadPick(items: Deliverable[]): Deliverable | undefined {
  if (items.length === 0) return undefined
  const newestFirst = [...items].reverse()

  return (
    newestFirst.find((item) => isReviewable(item.status)) ??
    newestFirst.find((item) => item.status !== 'Approved' && item.status !== 'Delivered') ??
    newestFirst.find((item) => Boolean(toDrivePreviewUrl(mediaLink(item)))) ??
    items[0]
  )
}

/** Anything the client is being asked to do, in one sentence, or ''. */
export function waitingOn(items: Deliverable[]): string {
  const waiting = items.filter((item) => isReviewable(item.status))
  if (waiting.length === 0) return ''
  if (waiting.length === 1) return `${waiting[0].name} is ready for you to watch and approve.`
  return `${waiting.length} films are ready for you to watch and approve.`
}

/**
 * The line under a tile. Says where the piece is in its own words, and for
 * an approved one says so plainly rather than going blank — a tile with no
 * state reads as unfinished.
 *
 * **"PLAYING NOW" NEEDS SOMETHING TO PLAY. 17 Oct 2026.**
 *
 * `active` means this tile is the one in the lead position, and that used
 * to be the whole test. On a job that has just been set up, nothing has
 * been uploaded yet — every piece is Planned and no tile has a file behind
 * it — and the lead tile still announced *Playing now* under an empty black
 * frame. Tom caught it on the first client page he generated end to end.
 *
 * A client reading that reasonably concludes something is there and their
 * browser has failed. The honest line on a planned piece is that it is
 * planned, whether or not it happens to be the one in the lead.
 */
export function tileState(item: Deliverable, active: boolean): string {
  if (active && mediaLink(item)) {
    return item.type === 'Photography' ? 'Showing now' : 'Playing now'
  }
  if (item.status === 'Approved') return 'Approved'
  if (item.status === 'Delivered') return 'Delivered'
  return clientStatusLabel(item.status)
}

/** "Your films" is wrong the moment a photography set is in the index. */
export function workTitle(items: Deliverable[]): string {
  if (items.length === 0) return 'Your work'
  const everyPhoto = items.every((item) => item.type === 'Photography')
  if (everyPhoto) return 'Your photographs'
  if (items.some((item) => item.type === 'Photography')) return 'Your work'
  return items.length === 1 ? 'Your film' : 'Your films'
}

/** A deliverable as a download row. */
export function fileRow(item: Deliverable, action: FileRow['action']): FileRow {
  if (item.type === 'Transcript') {
    return {
      id: item.id,
      label: item.name,
      meta: metaLabel(item),
      action: 'Open',
      opensInPortal: true,
      type: item.type,
    }
  }
  return {
    id: item.id,
    label: item.name,
    meta: metaLabel(item),
    action,
    type: item.type,
    driveUrl: mediaLink(item),
  }
}

export { canDownload, mediaLink }
